import {
	SIMPLE_STRUCTURE_TYPE,
	type TSimpleStructureType,
} from "@/models/objects/solidMesh/types";
/**
 * useSimpleStructureCreatorUi
 *
 * UI helper composable for SimpleStructureCreator.vue.
 * Provides v-model-compatible computed properties and i18n key helpers
 * derived from the useSimpleStructureCreation composable state.
 */
import { computed } from "vue";
import type { Store } from "vuex";
import { useSimpleStructureCreation } from "./useSimpleStructureCreation";

export function useSimpleStructureCreatorUi(store: Store<unknown>) {
	const creation = useSimpleStructureCreation(store);

	// -------------------------------------------------------------------------
	// Dialog open/close (v-model compatible: setter dispatches cancel)
	// -------------------------------------------------------------------------

	const showSelectionDialog = computed({
		get: () => creation.creationPhase.value === "selecting",
		set: (val: boolean) => {
			if (!val && creation.creationPhase.value === "selecting") {
				creation.cancelSimpleStructureCreation();
			}
		},
	});

	const showPropertyDialog = computed({
		get: () => creation.creationPhase.value === "confirming",
		set: (val: boolean) => {
			if (!val && creation.creationPhase.value === "confirming") {
				creation.cancelSimpleStructureCreation();
			}
		},
	});

	// -------------------------------------------------------------------------
	// Picker state
	// -------------------------------------------------------------------------

	const twoPointPickingStep = computed<0 | 1 | 2>(
		() => creation.creationTwoPointStep.value,
	);
	const pickedPosition = computed(() => creation.creationPickedPosition.value);
	const pickedTwoPointPositions = computed(
		() => creation.creationTwoPointPositions.value,
	);
	const selectedStructureType = computed<TSimpleStructureType | string>(
		() => store.state.solidMesh.selectedShapeType,
	);
	const tempMeshId = computed(() => creation.creationTempMeshId.value);

	// -------------------------------------------------------------------------
	// Structure-specific i18n key helpers for two-point step instructions
	// -------------------------------------------------------------------------

	const basisPointKey1 = computed<string>(() => {
		const type = selectedStructureType.value;
		if (type === SIMPLE_STRUCTURE_TYPE.PIPE) return "PIPE_BASIS_POINT_1";
		if (type === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL)
			return "WALL_BASIS_POINT_1";
		return "BASIS_POINT_1";
	});

	const basisPointKey2 = computed<string>(() => {
		const type = selectedStructureType.value;
		if (type === SIMPLE_STRUCTURE_TYPE.PIPE) return "PIPE_BASIS_POINT_2";
		if (type === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL)
			return "WALL_BASIS_POINT_2";
		return "BASIS_POINT_2";
	});

	return {
		showSelectionDialog,
		showPropertyDialog,
		twoPointPickingStep,
		pickedPosition,
		pickedTwoPointPositions,
		selectedStructureType,
		tempMeshId,
		basisPointKey1,
		basisPointKey2,
	};
}
